import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  Users, 
  CheckCircle,
  AlertCircle,
  Phone,
  MessageCircle,
  Loader,
  Video
} from 'lucide-react';
import { BrandingProvider } from '../../contexts/BrandingContext';
import { 
  BrandedHeader, 
  BrandedButton, 
  BrandedCard, 
  BrandedInput, 
  BrandedTextarea,
  BrandedBadge,
  BrandedAlert 
} from '../../components/branding/BrandedComponents';

interface GeneralRequestFormProps {
  companyName?: string;
  companyLogo?: string;
  organizationId?: string; // For API calls
}

interface RequestInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
}

interface RequestStatus {
  requestId: string;
  status: 'waiting' | 'accepted' | 'ready' | 'expired';
  clientName: string;
  companyName: string;
  createdAt: string;
  expiresAt: string;
  queuePosition?: number;
  estimatedWait?: string;
  meetingId?: string;
  roomId?: string;
}

export const GeneralRequestForm: React.FC<GeneralRequestFormProps> = ({
  companyName = 'Customer Connect',
  companyLogo,
  organizationId
}) => {
  // Require organizationId to be provided
  if (!organizationId) {
    console.error('🔴 GENERAL: organizationId is required for GeneralRequestForm');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Configuration Error</h2>
          <p className="text-gray-600">Organization ID is missing. Please check the URL.</p>
        </div>
      </div>
    );
  }
  
  return (
    <BrandingProvider templateType="general" initialBranding={{ companyName: companyName, companyLogo: companyLogo }}>
      <GeneralRequestFormContent 
        companyName={companyName}
        companyLogo={companyLogo}
        organizationId={organizationId}
      />
    </BrandingProvider>
  );
};

const GeneralRequestFormContent: React.FC<GeneralRequestFormProps> = ({
  companyName = 'Customer Connect',
  companyLogo,
  organizationId
}) => {
  const [step, setStep] = useState<'intake' | 'waiting' | 'ready' | 'error'>('intake');
  const [requestInfo, setRequestInfo] = useState<RequestInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [requestStatus, setRequestStatus] = useState<RequestStatus | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);

  // Submit request
  const submitRequest = async () => {
    // Validate required fields
    if (!requestInfo.firstName.trim()) {
      setError('Please enter your first name');
      return;
    }
    if (!requestInfo.lastName.trim()) {
      setError('Please enter your last name');
      return;
    }
    if (!requestInfo.email.trim()) {
      setError('Please enter your email address');
      return;
    }


    const fullName = `${requestInfo.firstName.trim()} ${requestInfo.lastName.trim()}`;
    
    const requestPayload = {
      organizationId,
      clientName: fullName,
      firmName: companyName,
      caseType: 'general_inquiry',
      urgency: 'medium',
      metadata: {
        firstName: requestInfo.firstName,
        lastName: requestInfo.lastName,
        phone: requestInfo.phone,
        email: requestInfo.email,
        notes: requestInfo.notes,
        requestType: 'general',
        templateType: 'general'
      }
    };

    console.log('🔵 GENERAL: Submitting request:', requestPayload);

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/consultation-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });

      console.log('🔵 GENERAL: API Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔴 GENERAL: API Error response:', errorText);
        throw new Error(`Failed to submit request: ${response.status}`);
      }

      const data = await response.json();
      console.log('🟢 GENERAL: Request submitted successfully:', data);
      console.log('🆔 GENERAL: Request ID generated:', data.requestId);
      if (data.meetingId) {
        console.log('🎥 GENERAL: Meeting ID:', data.meetingId);
      }
      
      setRequestId(data.requestId);
      setStep('waiting');
      
      // Set initial request status with meeting ID
      if (data.meetingId) {
        setRequestStatus({
          requestId: data.requestId,
          status: 'waiting',
          clientName: fullName,
          companyName: companyName,
          createdAt: new Date().toISOString(),
          expiresAt: data.expiresAt,
          queuePosition: data.queuePosition,
          estimatedWait: data.estimatedWait,
          meetingId: data.meetingId
        });
      }
      
      // Start polling for status updates
      startStatusPolling(data.requestId);

    } catch (err) {
      console.error('🔴 GENERAL: Error submitting request:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit request');
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Poll for request status updates
  const startStatusPolling = (reqId: string) => {
    console.log('🔄 GENERAL: Starting status polling for request ID:', reqId);
    
    const pollStatus = async () => {
      try {
        console.log('🔍 GENERAL: Polling status for request ID:', reqId);
        const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/consultation-status/${reqId}`);
        
        if (response.ok) {
          const statusData = await response.json();
          console.log('📊 GENERAL: Status update received:', statusData);
          setRequestStatus(statusData);
          
          // Check if request is ready
          if (statusData.status === 'accepted' || statusData.status === 'ready') {
            console.log('✅ GENERAL: Request accepted! Attempting to join...');
            // Try to join the session
            await tryJoinSession(reqId);
          }
        } else {
          console.warn('⚠️ GENERAL: Status polling failed with status:', response.status);
        }
      } catch (err) {
        console.error('🔴 GENERAL: Error polling status:', err);
      }
    };

    // Poll every 5 seconds
    const interval = setInterval(pollStatus, 5000);
    
    // Clean up interval after 1 hour
    setTimeout(() => clearInterval(interval), 60 * 60 * 1000);
    
    // Initial poll
    pollStatus();
  };

  // Try to join the session
  const tryJoinSession = async (reqId: string) => {
    try {
      console.log('🚪 GENERAL: Attempting to join session:', reqId);
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/join/${reqId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          participantName: `${requestInfo.firstName} ${requestInfo.lastName}`,
          role: 'participant'
        })
      });

      console.log('🚪 GENERAL: Join response status:', response.status);

      if (response.ok) {
        const joinData = await response.json();
        console.log('🎉 GENERAL: Successfully joined session:', joinData);
        setStep('ready');
        
        // Construct proper URL with all required parameters instead of relying on backend joinUrl
        const orgId = organizationId || 'org_1754742629431_tj1rxx'; // Use provided org ID or default
        // Use roomId from joinData response, fall back to requestStatus if needed
        const roomId = joinData.roomId || joinData.meetingId || requestStatus?.meetingId;
        const requestId = joinData.requestId || requestStatus?.requestId || reqId;
        const clientName = encodeURIComponent(`${requestInfo.firstName} ${requestInfo.lastName}`);
        
        console.log('🔧 GENERAL: Using session data:', {
          roomId,
          requestId, 
          clientName: `${requestInfo.firstName} ${requestInfo.lastName}`,
          joinData,
          requestStatus
        });
        
        if (!roomId) {
          console.error('🔴 GENERAL: No roomId available from joinData or requestStatus');
          console.error('🔴 GENERAL: joinData:', joinData);
          console.error('🔴 GENERAL: requestStatus:', requestStatus);
          return;
        }
        
        const properUrl = `${window.location.origin}/${orgId}/templates/general/session/participant?roomId=${roomId}&clientName=${clientName}&requestId=${requestId}&participantType=customer`;
        
        console.log('🔗 GENERAL: Redirecting to session room with proper parameters:', properUrl);
        console.log('🔗 GENERAL: Original backend URL was:', joinData.joinUrl);
        window.location.href = properUrl;
      } else {
        const errorText = await response.text();
        console.error('🔴 GENERAL: Failed to join session:', errorText);
      }
    } catch (err) {
      console.error('🔴 GENERAL: Error joining session:', err);
    }
  };

  const renderIntakeForm = () => (
    <div className="max-w-lg mx-auto">
      <BrandedHeader size="large" />

      <BrandedCard padding="large">
        <form onSubmit={(e) => { e.preventDefault(); submitRequest(); }}>
          <BrandedInput
            label="First Name *"
            type="text"
            value={requestInfo.firstName}
            onChange={(e) => setRequestInfo({...requestInfo, firstName: e.target.value})}
            placeholder="Enter your first name"
            required
          />

          <BrandedInput
            label="Last Name *"
            type="text"
            value={requestInfo.lastName}
            onChange={(e) => setRequestInfo({...requestInfo, lastName: e.target.value})}
            placeholder="Enter your last name"
            required
          />

          <BrandedInput
            label="Email Address *"
            type="email"
            value={requestInfo.email}
            onChange={(e) => setRequestInfo({...requestInfo, email: e.target.value})}
            placeholder="your.email@example.com"
            required
          />

          <BrandedInput
            label="Phone Number"
            type="tel"
            value={requestInfo.phone}
            onChange={(e) => setRequestInfo({...requestInfo, phone: e.target.value})}
            placeholder="+1 (555) 123-4567"
          />

          <BrandedTextarea
            label="Notes"
            value={requestInfo.notes}
            onChange={(e) => setRequestInfo({...requestInfo, notes: e.target.value})}
            placeholder="Please provide any additional information or notes..."
            rows={4}
          />

          {error && (
            <BrandedAlert variant="error" className="mb-6">
              {error}
            </BrandedAlert>
          )}

          <BrandedButton
            type="submit"
            disabled={isSubmitting}
            size="large"
            className="w-full"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <Loader className="animate-spin mr-2" size={20} />
                Submitting Request...
              </span>
            ) : (
              'Submit Request'
            )}
          </BrandedButton>

          <div className="mt-6 text-xs text-center" style={{ color: 'var(--brand-text-muted)' }}>
            <p>
              By submitting this form, you agree to our terms of service and privacy policy.
            </p>
          </div>
        </form>
      </BrandedCard>
    </div>
  );

  const renderWaitingRoom = () => (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-6 sm:mb-8">
        <BrandedHeader size="large" showTagline={false} />
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-2">
          Your request has been submitted
        </p>
      </div>

      <BrandedCard padding="large" className="mb-4 sm:mb-6">
        {/* Animated Status Icon */}
        <div className="flex items-center justify-center mb-6 sm:mb-8">
          <div className="relative">
            <div className="absolute inset-0 animate-ping rounded-full bg-blue-500 dark:bg-blue-400 opacity-20"></div>
            <div className="relative rounded-full bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-400 dark:to-blue-500 p-4 sm:p-5">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
        </div>

        <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
          Waiting for Agent to Accept
        </h2>

        {requestStatus && (
          <div className="space-y-4 sm:space-y-6">
            {/* Request Details Card */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="text-center sm:text-left">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Request ID</p>
                  <p className="font-mono text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                    {requestId?.split('_').pop()}
                  </p>
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Request Type</p>
                  <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                    General Inquiry
                  </p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Status</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                    <span className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full animate-pulse mr-1.5"></span>
                    {requestStatus.status.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* Queue Position & Wait Time - Mobile Optimized */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {requestStatus.queuePosition && (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Queue Position</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {requestStatus.queuePosition || '2'}
                  </p>
                </div>
              )}
              
              {requestStatus.estimatedWait && (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Est. Wait</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {requestStatus.estimatedWait || '5-10 minutes'}
                  </p>
                </div>
              )}
            </div>

            {/* Debug Info - Collapsible on Mobile */}
            {(requestStatus.meetingId || requestStatus.roomId) && (
              <details className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 sm:p-4 border border-blue-200 dark:border-blue-800">
                <summary className="text-xs sm:text-sm font-medium text-blue-800 dark:text-blue-300 cursor-pointer">
                  Debug Info - Meeting Room ID
                </summary>
                <p className="font-mono text-xs mt-2 text-blue-700 dark:text-blue-400 break-all">
                  {requestStatus.meetingId || requestStatus.roomId}
                </p>
              </details>
            )}
          </div>
        )}

        <div className="mt-6 sm:mt-8 text-center">
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            You will automatically be redirected when an agent is ready to meet with you.
          </p>
        </div>
      </BrandedCard>

      {/* What Happens Next - Mobile Optimized */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800 border border-blue-200 dark:border-gray-700 rounded-xl p-4 sm:p-6">
        <h3 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white mb-3 sm:mb-4 flex items-center">
          <span className="inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-blue-600 dark:bg-blue-500 text-white text-xs sm:text-sm mr-2">
            ?
          </span>
          What happens next?
        </h3>
        <div className="space-y-2 sm:space-y-3">
          {[
            'An agent will review your request',
            'When ready, they\'ll accept your request',
            'A video room will be created automatically',
            'You\'ll be redirected to join the session'
          ].map((step, index) => (
            <div key={index} className="flex items-start">
              <span className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 text-xs font-semibold mr-2 sm:mr-3 flex-shrink-0 mt-0.5">
                {index + 1}
              </span>
              <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">{step}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderReadyState = () => (
    <div className="max-w-2xl mx-auto text-center">
      <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Session Ready!</h1>
      <p className="text-gray-600 mb-8">An agent has accepted your request</p>

      <div className="bg-white rounded-xl shadow-lg p-8">
        <Video className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Redirecting to Video Session...
        </h2>
        <p className="text-gray-600">
          Please wait while we connect you to your agent.
        </p>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="max-w-2xl mx-auto text-center">
      <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Request Failed</h1>
      <p className="text-gray-600 mb-8">There was an error submitting your request</p>

      <div className="bg-white rounded-xl shadow-lg p-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <button
          onClick={() => { setStep('intake'); setError(null); }}
          className="bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: 'var(--brand-background)' }}>
      {step === 'intake' && renderIntakeForm()}
      {step === 'waiting' && renderWaitingRoom()}
      {step === 'ready' && renderReadyState()}
      {step === 'error' && renderErrorState()}
    </div>
  );
};
